// Utility for PDF export using html2canvas and jsPDF
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Audio recording utilities
export function getSupportedMimeType() {
  const types = [
    'audio/mp4',
    'audio/mpeg',
    'audio/wav',
    'audio/webm',
    'audio/ogg',
  ];
  for (const type of types) {
    if (window.MediaRecorder && window.MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return '';
}

// Returns a Promise that resolves to {recorder, mimeType, stream, chunks}
export async function startRecording(onStopCallback) {
  if (!navigator.mediaDevices || !window.MediaRecorder) {
    throw new Error('Recording not supported in this browser.');
  }
  const mimeType = getSupportedMimeType();
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const recorder = mimeType ? new window.MediaRecorder(stream, { mimeType }) : new window.MediaRecorder(stream);
  let chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  recorder.onstop = () => {
    const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
    if (onStopCallback) onStopCallback(blob, mimeType);
  };
  recorder.start();
  return { recorder, mimeType, stream, chunks };
}


export async function downloadStoryAsPDF(storyTitle, allPagesRef) {
  if (!allPagesRef.current) return;

  // Give Chromium one rendered frame after fonts/layout settle before cloning
  // the off-screen pages. Safari generally does this implicitly.
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }
  await new Promise(resolve => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));

  const BRAND_URL = 'https://stories.marstrix.com';
  const BRAND_LABEL = 'stories.marstrix.com';

  const pageNodes = allPagesRef.current.querySelectorAll('.pdf-story-page');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

  const addBrandLink = () => {
    const x = 18;
    const y = 22;
    const fontSize = 10;

    pdf.setFontSize(fontSize);
    pdf.setTextColor(0, 0, 0); // black

    const textWidth = pdf.getTextWidth(BRAND_LABEL);

    // Small white background to keep the link readable regardless of the page image
    pdf.setFillColor(255, 255, 255);
    pdf.rect(x - 6, y - fontSize - 4, textWidth + 12, fontSize + 10, 'F');

    if (typeof pdf.textWithLink === 'function') {
      pdf.textWithLink(BRAND_LABEL, x, y, { url: BRAND_URL });
      return;
    }

    // Fallback: draw text + add a clickable link rectangle
    pdf.text(BRAND_LABEL, x, y);
    if (typeof pdf.link === 'function') {
      pdf.link(x, y - fontSize, textWidth, fontSize + 2, { url: BRAND_URL });
    }
  };

  for (let i = 0; i < pageNodes.length; i++) {
    const node = pageNodes[i];
    // eslint-disable-next-line no-await-in-loop
    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      allowTaint: false,
      scrollY: 0,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    if (i > 0) pdf.addPage();

    // Preserve the captured page's aspect ratio instead of stretching it to A4.
    const canvasRatio = canvas.width / canvas.height;
    const pdfRatio = pdfWidth / pdfHeight;
    let renderWidth = pdfWidth;
    let renderHeight = pdfHeight;
    let renderX = 0;
    let renderY = 0;
    if (canvasRatio > pdfRatio) {
      renderHeight = pdfWidth / canvasRatio;
      renderY = (pdfHeight - renderHeight) / 2;
    } else if (canvasRatio < pdfRatio) {
      renderWidth = pdfHeight * canvasRatio;
      renderX = (pdfWidth - renderWidth) / 2;
    }
    pdf.addImage(imgData, 'JPEG', renderX, renderY, renderWidth, renderHeight);
    addBrandLink();
  }

  pdf.save(`${storyTitle || 'storybook'}.pdf`);
}
